import { IconButton, Typography } from 'omakase';
import { pluralize } from '@tock/shared/utils/pluralize';
import BaseComponent from '@tock/shared/components/BaseComponent';

import { IconMinus, IconPlusLarge } from '@tock/icons';
import { MAX_BOOKING_GUEST_SIZE } from 'constants/BookingConstants';
import React from 'react';

interface Props {
  disabled?: boolean;
  hasInitialFocus?: boolean;
  maxSize: number;
  minSize: number;
  onDecrement(): void;
  onIncrement(): void;
  selectedSize: number;
  partySizeUnit?: string;
}

export default class PurchaseSizeSelector extends BaseComponent<Props> {
  decrementButtonRef: React.RefObject<HTMLButtonElement>;
  incrementButtonRef: React.RefObject<HTMLButtonElement>;

  getDisableDecrement() {
    const { minSize, selectedSize, disabled } = this.props;
    return disabled || selectedSize <= minSize;
  }

  getDisableIncrement() {
    const { maxSize, selectedSize, disabled } = this.props;
    return disabled || selectedSize >= Math.min(maxSize, MAX_BOOKING_GUEST_SIZE);
  }

  handleDecrement() {
    if (!this.getDisableDecrement()) {
      this.props.onDecrement();
    }
  }

  handleIncrement() {
    if (!this.getDisableIncrement()) {
      this.props.onIncrement();
    }
  }

  constructor(props: Props) {
    super(props);

    this.decrementButtonRef = React.createRef();
    this.incrementButtonRef = React.createRef();
  }

  // We do the focus-related componentDidMount only in a setTimeout after 600 ms,
  // because this content may be presented in an iframe. If it is, and:
  //
  // * If voiceover is turned on, and the user is using Safari, some kind of
  // setTimeout delay is necessary to avoid a problem where the focus gets trapped
  // outside the dialog. Any amount of delay seems to be sufficient here.
  //
  // * If voiceover is turned on, and the user is using Chrome, focusing one of
  // these buttons too soon causes an issue where navigating the page content
  // will loop infinitely within a small part of the page instead of including
  // all pageable elements. For this problem, a small delay is not enough;
  // experimentally, 600 ms is consistently sufficient.
  componentDidMount() {
    const { disabled, hasInitialFocus } = this.props;
    window.setTimeout(() => {
      if (!disabled && hasInitialFocus) {
        if (!this.decrementButtonRef.current?.disabled) {
          this.decrementButtonRef.current?.focus();
        } else if (!this.incrementButtonRef.current?.disabled) {
          this.incrementButtonRef.current?.focus();
        }
      }
    }, 600);
  }

  componentDidUpdate() {
    // No work to do here, just quiets a warning about our use of
    // getSnapshotBeforeUpdate()
  }

  // We use this handler to move the focus between the increment/decrement
  // buttons if the focused one is just about to disable.
  getSnapshotBeforeUpdate({ selectedSize: prevSelectedSize }: Props) {
    const { maxSize, minSize, selectedSize } = this.props;
    const incrementButton = this.incrementButtonRef.current;
    const decrementButton = this.decrementButtonRef.current;
    if (
      incrementButton &&
      prevSelectedSize === minSize + 1 &&
      selectedSize === minSize &&
      document.activeElement === this.decrementButtonRef.current
    ) {
      // The decrement button is focused but is about to be disabled.
      // Move the focus to the increment button:
      // A quick hack: the increment button may be disabled at the moment (e.g.,
      // allowable guest counts are 2 or 3), but it will enable on the next render.
      // Do it now:
      incrementButton.disabled = false;
      incrementButton.focus();
    } else if (
      decrementButton &&
      prevSelectedSize === maxSize - 1 &&
      selectedSize === maxSize &&
      document.activeElement === this.incrementButtonRef.current
    ) {
      // Similar as above
      decrementButton.disabled = false;
      decrementButton.focus();
    }

    return null;
  }

  render() {
    const { selectedSize, partySizeUnit = 'guest' } = this.props;

    const selectedSizeText = pluralize(selectedSize || 1, partySizeUnit);
    const currentSizeText = `current party size is ${selectedSize}`;
    const fewerText = `Fewer ${partySizeUnit}s, ${currentSizeText}`;
    const moreText = `More ${partySizeUnit}s, ${currentSizeText}`;

    return (
      <div className="GuestSelector" data-testid="guest-selector">
        <div
          className="GuestSelector-text"
          data-testid="guest-selector-text"
          selected-value={selectedSize}
          aria-live="polite"
        >
          <Typography variant="caption" color="fgSubtle">
            Party size
          </Typography>
          <Typography variant="body02" color="fgDefault">
            {selectedSizeText}
          </Typography>
        </div>

        <div className="GuestSelector-buttons">
          <IconButton
            disabled={this.getDisableDecrement()}
            className="GuestSelector-button"
            variant="ghost"
            size="large"
            onClick={this.handleDecrement}
            data-testid="guest-selector_minus"
            aria-label={fewerText}
            ref={this.decrementButtonRef}
          >
            <IconMinus />
          </IconButton>

          <IconButton
            disabled={this.getDisableIncrement()}
            className="GuestSelector-button"
            variant="ghost"
            size="large"
            onClick={this.handleIncrement}
            data-testid="guest-selector_plus"
            aria-label={moreText}
            ref={this.incrementButtonRef}
          >
            <IconPlusLarge />
          </IconButton>
        </div>
      </div>
    );
  }
}
